import {
  type Accessor,
  createEffect,
  createMemo,
  createSignal,
  on,
  onCleanup,
  onMount,
} from "solid-js";
import { bringNodeIntoView, clampView } from "./estateFormat";
import type { EstateLayout, EstateNode, ViewTransform } from "./estateTypes";
import { MAX_ZOOM } from "./estateTypes";
import { zoomTransform } from "./estateViewport";

/**
 * Pan/zoom state and pointer handlers for the navigable estate map. Owns the
 * view transform, auto-fit and selection-follow effects; the component keeps
 * hover state and passes its setter so panning can clear the tooltip.
 */
export function createEstateViewport(input: {
  compact: Accessor<boolean>;
  layout: Accessor<EstateLayout>;
  fitView: Accessor<ViewTransform>;
  nodeById: Accessor<Map<string, EstateNode>>;
  selectedPageId: Accessor<string | null>;
  setHoveredPageId: (pageId: string | null) => void;
}) {
  let panStart: {
    pointerId: number;
    clientX: number;
    clientY: number;
    view: ViewTransform;
  } | null = null;
  const [isPanning, setIsPanning] = createSignal(false);
  const [hasFitted, setHasFitted] = createSignal(false);
  const [view, setView] = createSignal<ViewTransform>({ x: 0, y: 0, scale: 1 });
  const minZoom = createMemo(() =>
    Math.max(0.18, input.fitView().scale * 0.72),
  );

  onMount(() => {
    const clearOutsideNode = (event: PointerEvent | MouseEvent) => {
      const target = event.target;
      if (target instanceof Element && target.closest(".signal-estate-node")) {
        return;
      }
      input.setHoveredPageId(null);
    };
    document.addEventListener("pointermove", clearOutsideNode);
    document.addEventListener("mousemove", clearOutsideNode);
    onCleanup(() => {
      document.removeEventListener("pointermove", clearOutsideNode);
      document.removeEventListener("mousemove", clearOutsideNode);
    });
  });

  const effectiveView = createMemo(() =>
    input.compact() ? input.fitView() : view(),
  );
  const worldTransform = createMemo(
    () =>
      `translate(${effectiveView().x} ${effectiveView().y}) scale(${effectiveView().scale})`,
  );
  const zoomPercent = createMemo(
    () => `${Math.round(effectiveView().scale * 100)}%`,
  );

  const fitEstate = () => {
    setView(input.fitView());
    setHasFitted(true);
  };
  const zoomAt = (viewportX: number, viewportY: number, factor: number) => {
    if (input.compact()) {
      return;
    }
    setView((current) =>
      clampView(
        zoomTransform(
          current,
          viewportX,
          viewportY,
          factor,
          minZoom(),
          MAX_ZOOM,
        ),
        input.layout(),
      ),
    );
  };
  const zoomBy = (factor: number) => {
    const viewport = input.layout().viewport;
    zoomAt(viewport.width / 2, viewport.height / 2, factor);
  };

  createEffect(() => {
    if (input.compact() || hasFitted()) {
      return;
    }
    fitEstate();
  });
  createEffect(
    on(
      input.selectedPageId,
      (pageId) => {
        if (input.compact() || !hasFitted()) {
          return;
        }
        const node = pageId ? input.nodeById().get(pageId) : null;
        if (!node) {
          return;
        }
        setView((current) => bringNodeIntoView(current, node, input.layout()));
      },
      { defer: true },
    ),
  );

  const svgHandlers = {
    onWheel(event: WheelEvent & { currentTarget: SVGSVGElement }) {
      if (input.compact()) {
        return;
      }
      event.preventDefault();
      const rect = event.currentTarget.getBoundingClientRect();
      const viewport = input.layout().viewport;
      const viewportX =
        ((event.clientX - rect.left) / rect.width) * viewport.width;
      const viewportY =
        ((event.clientY - rect.top) / rect.height) * viewport.height;
      const sensitivity = event.ctrlKey ? 0.0062 : 0.0012;
      zoomAt(viewportX, viewportY, Math.exp(-event.deltaY * sensitivity));
    },
    onPointerDown(event: PointerEvent & { currentTarget: SVGSVGElement }) {
      if (input.compact()) {
        return;
      }
      const target = event.target;
      if (target instanceof Element && target.closest(".signal-estate-node")) {
        return;
      }
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      panStart = {
        pointerId: event.pointerId,
        clientX: event.clientX,
        clientY: event.clientY,
        view: view(),
      };
      setIsPanning(true);
      input.setHoveredPageId(null);
    },
    onPointerMove(event: PointerEvent & { currentTarget: SVGSVGElement }) {
      if (!panStart || panStart.pointerId !== event.pointerId) {
        return;
      }
      const rect = event.currentTarget.getBoundingClientRect();
      const viewport = input.layout().viewport;
      const deltaX =
        ((event.clientX - panStart.clientX) / rect.width) * viewport.width;
      const deltaY =
        ((event.clientY - panStart.clientY) / rect.height) * viewport.height;
      setView(
        clampView(
          {
            ...panStart.view,
            x: panStart.view.x + deltaX,
            y: panStart.view.y + deltaY,
          },
          input.layout(),
        ),
      );
    },
    onPointerUp(event: PointerEvent & { currentTarget: SVGSVGElement }) {
      if (!panStart || panStart.pointerId !== event.pointerId) {
        return;
      }
      event.currentTarget.releasePointerCapture(event.pointerId);
      panStart = null;
      setIsPanning(false);
    },
    onPointerCancel(event: PointerEvent & { currentTarget: SVGSVGElement }) {
      if (panStart?.pointerId === event.pointerId) {
        panStart = null;
        setIsPanning(false);
      }
    },
  };

  return {
    view,
    effectiveView,
    isPanning,
    worldTransform,
    zoomPercent,
    zoomBy,
    fitEstate,
    svgHandlers,
  };
}
